import React from 'react';
import { BrowserRouter as Router, Routes, Route, Outlet } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import Layout from './components/Layout';
import Home from './pages/Home';
import About from './pages/About';
import Services from './pages/Services';
import Career from './pages/Career';
import Contact from './pages/Contact';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import Login from './pages/Login';
import Register from './pages/Register';
import Profile from './pages/Profile';
import ResetPassword from './pages/ResetPassword';
import ResetPasswordConfirm from './pages/ResetPasswordConfirm';
import AdminPanel from './pages/AdminPanel';
import AdminBlog from './pages/AdminBlog';
import AdminContent from './pages/AdminContent';
import BlogEditor from './components/BlogEditor';
import BlogByTag from './pages/BlogByTag';
import HomeAi from './pages/ai/HomeAi';
import AboutAi from './pages/ai/AboutAi';
import ServicesAi from './pages/ai/ServicesAi';
import ContactAi from './pages/ai/ContactAi';
import AiLayout from './components/ai/AiLayout';
import { getCurrentDomain, getApiUrl } from './config/domains';
import './styles/App.css';
import './styles/themes/ai.css';

const App: React.FC = () => {
  const domain = getCurrentDomain();
  const isAi = domain.id === 'ai';
  const LayoutComponent = isAi ? AiLayout : Layout;

  // Set CSS custom properties for domain colors and update meta tags
  React.useEffect(() => {
    document.documentElement.style.setProperty('--primary-color', domain.primaryColor);
    document.documentElement.style.setProperty('--secondary-color', domain.secondaryColor);
    document.documentElement.style.setProperty('--font-family', domain.fontFamily || '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif');
    document.title = domain.name;

    // Update favicon
    if (domain.faviconPath) {
      const favicon = document.getElementById('favicon') as HTMLLinkElement;
      if (favicon) {
        favicon.href = domain.faviconPath;
      }
    }

    // Update theme color
    const themeColor = document.getElementById('theme-color') as HTMLMetaElement;
    if (themeColor) {
      themeColor.content = domain.primaryColor;
    }

    // Update description
    const description = document.getElementById('description') as HTMLMetaElement;
    if (description) {
      description.content = `${domain.fullName} - ${domain.tagline}`;
    }
  }, [domain]);

  // Override --font-family with the value saved in the CMS admin (if any).
  // The static stack from domains.ts is already applied above; this runs
  // asynchronously and silently no-ops on failure, so the page never blocks
  // on the backend being reachable.
  React.useEffect(() => {
    let cancelled = false;
    const apply = async () => {
      try {
        const res = await fetch(`${getApiUrl()}/api/cms/domains/${domain.id}/font-config`);
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (cancelled) return;
        if (data?.fontFamily) {
          document.documentElement.style.setProperty('--font-family', data.fontFamily);
        }
      } catch {
        // Backend unreachable — static fallback stays in effect.
      }
    };
    apply();
    return () => { cancelled = true; };
  }, [domain]);

  return (
    <AuthProvider>
      <Router>
        <Routes>
          {/* Public marketing routes — get AiLayout (dark) on .ai, legacy Layout elsewhere */}
          <Route element={<LayoutComponent><Outlet /></LayoutComponent>}>
            <Route path="/" element={isAi ? <HomeAi /> : <Home />} />
            <Route path="/about" element={isAi ? <AboutAi /> : <About />} />
            <Route path="/services" element={isAi ? <ServicesAi /> : <Services />} />
            <Route path="/career" element={<Career />} />
            <Route path="/contact" element={isAi ? <ContactAi /> : <Contact />} />
            {domain.hasBlog && (
              <>
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/tag/:tag" element={<BlogByTag />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
              </>
            )}
            <Route path="*" element={isAi ? <HomeAi /> : <Home />} />
          </Route>
          {/* Auth + admin routes — always legacy light Layout, even on .ai (B3 invariant) */}
          <Route element={<Layout><Outlet /></Layout>}>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/reset-password/confirm" element={<ResetPasswordConfirm />} />
            <Route path="/admin" element={<AdminPanel />} />
            <Route path="/admin/content" element={<AdminContent />} />
            {domain.hasBlog && (
              <>
                <Route path="/admin/blog" element={<AdminBlog />} />
                <Route path="/admin/blog/new" element={<BlogEditor mode="create" />} />
                <Route path="/admin/blog/edit/:slug" element={<BlogEditor mode="edit" />} />
              </>
            )}
          </Route>
        </Routes>
      </Router>
    </AuthProvider>
  );
};

export default App;
